import { useState, useEffect, useContext, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  Divider, 
  CircularProgress, 
  Alert,
  Fade 
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import axios from 'axios';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ScrollContext } from '../Context';
import { ThemeContext } from '../Context';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';

// Composant pour transformer la valeur du type de police
function TypeDisplay() {
  const [typeValue, setTypeValue] = useState('');

  useEffect(() => {
    fetch('/api/settings')
      .then((response) => response.json())
      .then((data) => {
        if (data['type'] !== undefined) {
          if (data['type'] === 'local') {
            setTypeValue('Locale');
          } else if (data['type'] === 'federal') {
            setTypeValue('Fédérale');
          } else {
            setTypeValue(data['type']);
          }
        } else {
          console.error('Le champ type est manquant dans les données récupérées.');
        }
      })
      .catch((error) => console.error('Erreur lors de la récupération du type :', error));
  }, []);

  return typeValue || '...';
}

// Composant pour afficher le contenu HTML avec la mise en forme
function RichTextDisplay({ content, isDark }) {
  if (!content) return (
    <Typography 
      component="span" 
      sx={{ 
        color: isDark ? '#95a5a6' : 'text.disabled',
        display: 'block',
        width: '100%',
        textAlign: 'center',
        fontStyle: 'italic'
      }}
    >
      Cette rubrique est vide.
    </Typography>
  );

  return (
    <Box 
      sx={{
        '& .rich-text-content': {
          color: isDark ? '#ecf0f1' : 'inherit',
          '& h1, & h2, & h3': {
            margin: '0.5em 0',
            fontWeight: 'bold',
            color: isDark ? '#ecf0f1' : 'inherit'
          },
          '& h1': { fontSize: '1.425em' },
          '& h2': { fontSize: '1.235em' },
          '& h3': { fontSize: '1.045em' },
          '& p': {
            margin: '0.5em 0',
            fontSize: '15px',
            color: isDark ? '#ecf0f1' : 'inherit'
          },
          '& ul, & ol': { 
            paddingLeft: '2em',
            margin: '0.5em 0',
            color: isDark ? '#ecf0f1' : 'inherit'
          },
          '& blockquote': {
            borderLeft: isDark ? '3px solid #4fc3f7' : '3px solid #ccc',
            paddingLeft: '1em',
            margin: '0.5em 0',
            fontStyle: 'italic',
            color: isDark ? '#bdc3c7' : 'inherit'
          },
          '& img': {
            maxWidth: '100%'
          },
          '& a': {
            color: isDark ? '#4fc3f7' : 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }
        }
      }}
    >
      <div 
        dangerouslySetInnerHTML={{ __html: content }} 
        style={{ 
          fontFamily: 'inherit',
          overflow: 'visible'
        }}
        className="rich-text-content"
      />
    </Box>
  );
}

function Read() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { targetRubric, scrollToBottomTrigger } = useContext(ScrollContext);
  const { theme } = useContext(ThemeContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';
  
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Références aux rubriques pour le défilement
  const rubricRefs = useRef({});
  const containerRef = useRef(null);

  // Charger les données du rapport
  useEffect(() => {
    if (!report) return;

    const fetchReportData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setReportData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données du rapport:', error);
        setError('Impossible de charger les rubriques du rapport. Veuillez réessayer.');
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Écouter les mises à jour du rapport via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdate = (updatedReport) => {
      console.log('Mise à jour de rapport reçue:', updatedReport);
      
      if (updatedReport.number === report.number && updatedReport.year === report.year) {
        // Mise à jour de tous les champs reçus
        setReportData(prevData => {
          if (!prevData) return null;
          return {
            ...prevData,
            ...updatedReport
          };
        });
      }
    };
    
    // Écouter également les événements émis directement par le client
    const handleDirectUpdate = (data) => {
      console.log('Mise à jour directe reçue:', data);
      
      if (data.reportId === `${report.number}-${report.year}`) {
        console.log('Mise à jour directe des données:', data);
        setReportData(prevData => {
          if (!prevData) return null;
          // Créer un objet avec les champs à mettre à jour
          const updates = {};
          
          // Liste des champs à mettre à jour
          const fieldsToUpdate = [
            'rubrics', 'StartDate', 'EndDate', 'intervenants', 
            'unit', 'reportType', 'indice', 'personnesPhysiques', 
            'objets', 'vehicules'
          ];
          
          fieldsToUpdate.forEach(field => {
            if (data[field] !== undefined) {
              updates[field] = data[field];
            }
          });
          
          return {
            ...prevData,
            ...updates
          };
        });
      }
    };

    socket.on('reportUpdated', handleReportUpdate);
    socket.on('updateReport', handleDirectUpdate);

    // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***

    return () => {
      socket.off('reportUpdated', handleReportUpdate);
      socket.off('updateReport', handleDirectUpdate);
      // *** SUPPRIMÉ : Plus de joinReport/leaveReport individuel - géré par Core.js ***
    };
  }, [socket, report]);

  // Gérer le défilement vers la rubrique ciblée
  useEffect(() => {
    if (targetRubric && rubricRefs.current[targetRubric]) {
      rubricRefs.current[targetRubric].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [targetRubric]);

  // Gérer le scroll vers le bas quand une nouvelle rubrique est ajoutée
  useEffect(() => {
    if (scrollToBottomTrigger > 0 && containerRef.current) {
      // Petit délai pour s'assurer que le DOM est mis à jour
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 50);
    }
  }, [scrollToBottomTrigger]);

      // Fonction pour formater la date et heure de début (StartDate)
      const formatStartDate = (dateString) => {
        if (!dateString) return 'Non défini';
        
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString;
          
          // Format de date: jour mois année
          const dateFormatee = date.toLocaleDateString('fr-FR', {
            weekday: "long",
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
          
          // Heure et minutes au format HH:MM
          const heures = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          const heureFormatee = `${heures}:${minutes}`;
          
          // Ajouter un "s" à "heure" si l'heure est 2 ou plus
          const pluriel = date.getHours() >= 2 ? 's' : '';
          
          return `${dateFormatee} à ${heureFormatee} heure${pluriel}`;
        } catch (error) {
          console.error('Erreur lors du formatage de la date de début:', error);
          return dateString;
        }
      };
    
      // Fonction pour formater la date de fin (EndDate)
      const formatEndDate = (dateString) => {
        if (!dateString) return 'Non défini';
        
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString;
          
          // Format de date: jour mois année
          return date.toLocaleDateString('fr-FR', {
            weekday: "long",
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
        } catch (error) {
          console.error('Erreur lors du formatage de la date de fin:', error);
          return dateString;
        }
      };

  // Afficher un indicateur de chargement
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <CircularProgress sx={{
          color: isDark ? '#4fc3f7' : undefined
        }} />
      </Box>
    );
  }

  // Afficher un message d'erreur
  if (error) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <Alert 
          severity="error"
          sx={{
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ff6b6b' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ff6b6b' : undefined
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Afficher un message si aucun rapport n'est sélectionné
  if (!report) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <Alert 
          severity="info"
          sx={{
            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : undefined,
            color: isDark ? '#4fc3f7' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#4fc3f7' : undefined
            }
          }}
        >
          Veuillez sélectionner un rapport pour afficher son contenu.
        </Alert>
      </Box>
    );
  }

  // Afficher un message si aucune rubrique n'est disponible
  if (!reportData || !reportData.rubrics || reportData.rubrics.length === 0) {
    const steps = [
      { number: 1, text: 'Effectuez vos encodages' },
      { number: 2, text: 'Ajoutez des rubriques ou un canevas' },
      { number: 3, text: 'Visionnez-les ici' }
    ];

    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : '#f5f7fa',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Quadrillage en arrière-plan avec diffusion */}
        <Box sx={{
          position: 'absolute',
          width: 600,
          height: 600,
          backgroundImage: isDark 
            ? `linear-gradient(rgba(79, 195, 247, 0.12) 1px, transparent 1px),
               linear-gradient(90deg, rgba(79, 195, 247, 0.12) 1px, transparent 1px)`
            : `linear-gradient(rgba(45, 64, 122, 0.2) 1px, transparent 1px),
               linear-gradient(90deg, rgba(45, 64, 122, 0.2) 1px, transparent 1px)`,
          backgroundSize: '35px 35px',
          maskImage: 'radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(circle, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 40%, transparent 75%)',
          animation: 'gridPulse 4s ease-in-out infinite',
          '@keyframes gridPulse': {
            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.8, transform: 'scale(1.02)' }
          }
        }} />

        {/* Conteneur principal */}
        <Fade in={true} timeout={600}>
          <Box sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: 400
          }}>
            {/* Icône principale animée */}
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '20px',
              background: isDark 
                ? 'linear-gradient(135deg, rgb(37, 53, 100) 0%, rgb(52, 74, 141) 100%)'
                : 'linear-gradient(135deg, #1a253c 0%, #2d407a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: isDark 
                ? '0 10px 40px rgba(52, 74, 141, 0.4)'
                : '0 10px 40px rgba(26, 37, 60, 0.35)',
              animation: 'iconPulse 3s ease-in-out infinite',
              '@keyframes iconPulse': {
                '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
                '50%': { transform: 'scale(1.05) rotate(2deg)' }
              }
            }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 40, color: '#fff' }} />
            </Box>

            {/* Titre */}
            <Typography 
              variant="h5" 
              sx={{ 
                color: isDark ? '#ecf0f1' : '#2c3e50',
                fontWeight: 600,
                textAlign: 'center',
                mb: 4
              }}
            >
              Document vide
            </Typography>

            {/* Étapes avec animation */}
            <Box sx={{ width: '100%' }}>
              {steps.map((step, index) => (
                <Fade 
                  key={step.number} 
                  in={true} 
                  timeout={800}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      mb: 1.5,
                      borderRadius: '12px',
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
                      border: `1px solid ${isDark ? 'rgba(52, 74, 141, 0.25)' : 'rgba(45, 64, 122, 0.15)'}`,
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      cursor: 'default',
                      animation: `slideIn 0.5s ease-out ${index * 0.15}s both`,
                      '@keyframes slideIn': {
                        from: { opacity: 0, transform: 'translateX(-20px)' },
                        to: { opacity: 1, transform: 'translateX(0)' }
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        backgroundColor: isDark ? 'rgba(52, 74, 141, 0.12)' : 'rgba(45, 64, 122, 0.08)',
                        borderColor: isDark ? 'rgba(52, 74, 141, 0.4)' : 'rgba(45, 64, 122, 0.25)',
                        boxShadow: isDark 
                          ? '0 4px 20px rgba(52, 74, 141, 0.2)'
                          : '0 4px 20px rgba(45, 64, 122, 0.15)'
                      }
                    }}
                  >
                    {/* Numéro avec cercle */}
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: isDark 
                        ? 'linear-gradient(135deg, rgb(37, 53, 100) 0%, rgb(52, 74, 141) 100%)'
                        : 'linear-gradient(135deg, #1a253c 0%, #2d407a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                      boxShadow: isDark 
                        ? '0 4px 12px rgba(52, 74, 141, 0.4)'
                        : '0 4px 12px rgba(26, 37, 60, 0.3)'
                    }}>
                      <Typography sx={{ 
                        color: '#fff', 
                        fontWeight: 700, 
                        fontSize: '0.95rem' 
                      }}>
                        {step.number}
                      </Typography>
                    </Box>

                    {/* Texte */}
                    <Typography sx={{
                      color: isDark ? '#ecf0f1' : '#2c3e50',
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      flex: 1
                    }}>
                      {step.text}
                    </Typography>
                  </Box>
                </Fade>
              ))}
            </Box>
          </Box>
        </Fade>
      </Box>
    );
  }

  // Tri des rubriques par position
  const sortedRubrics = [...reportData.rubrics].sort((a, b) => a.position - b.position);

  // Rendu du composant avec les rubriques
  return (
    <Box 
      ref={containerRef}
      sx={{ 
        p: 3, 
        height: '100%', 
        overflow: 'auto',
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit',
        color: isDark ? '#ecf0f1' : 'inherit',
      // Styles pour les scrollbars en mode sombre
      '&::-webkit-scrollbar': {
        width: isDark ? '8px' : undefined,
        height: isDark ? '8px' : undefined,
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: isDark ? '#1a252f' : undefined,
        borderRadius: isDark ? '4px' : undefined,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: isDark ? '#2d3f4c' : undefined,
        borderRadius: isDark ? '4px' : undefined,
        '&:hover': {
          backgroundColor: isDark ? '#384550' : undefined,
        },
      },
      // Pour Firefox
      scrollbarWidth: isDark ? 'thin' : undefined,
      scrollbarColor: isDark ? '#2d3f4c #1a252f' : undefined,
    }}>
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom sx={{
          color: isDark ? '#ecf0f1' : 'inherit'
        }}>
           P&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;R&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;O&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;J&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;U&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;S&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;T&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;I&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;T&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;I&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;A
         </Typography>
      </Box>
      <hr style={{ 
        borderColor: isDark ? '#2d3f4c' : undefined 
      }}/>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="p" gutterBottom align="left" sx={{
          color: isDark ? '#ecf0f1' : 'inherit'
        }}>
           Police <TypeDisplay /><br/>
           <SettingsFieldDisplay fieldName="clientName" /> - <SettingsFieldDisplay fieldName="zoneNumber" /><br/>
           Service {reportData.unit}<br/>
        </Typography>
        <Typography variant="p" gutterBottom align="right" sx={{
          color: isDark ? '#ecf0f1' : 'inherit'
        }}>
           Procès-verbal {reportData.reportType === "subsequent" ? "subséquent" : reportData.reportType}<br/>
           <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.
           {reportData.indice || '  '}.
           <SettingsFieldDisplay fieldName="judicialDivision" />.
           {reportData.number}/
           {reportData.year}<br/>
           Suite 1 sur ?
        </Typography>
      </Box>
      <hr style={{ 
        borderColor: isDark ? '#2d3f4c' : undefined 
      }}/>
      <br/>
      <Typography variant="body1" sx={{
        whiteSpace: 'pre-wrap',
        '& p': { marginTop: 0 },
        textAlign: 'center',
        fontSize: '15px',
        color: isDark ? '#ecf0f1' : 'inherit'
      }}>
        Ce jourd'hui, le {formatStartDate(reportData.StartDate)},<br/>
        Nous soussigné{reportData.intervenants && 
          reportData.intervenants.filter(intervenant => intervenant.sign === true).length > 1 ? 's' : ''}, {reportData.intervenants && reportData.intervenants
          .filter(intervenant => intervenant.sign === true)
          .map((intervenant, index, array) => (
            <Typography component="span" key={index}>
              <Typography component="span" sx={{
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontSize: '15px',
                color: isDark ? '#ecf0f1' : 'inherit'
              }}>
                {intervenant.lastname}
              </Typography>
              {' '}
              <Typography component="span" sx={{
                fontWeight: 'bold',
                fontSize: '15px',
                color: isDark ? '#ecf0f1' : 'inherit'
              }}>
                {intervenant.firstname}
              </Typography>
              <Typography component="span" sx={{
                fontSize: '15px',
                color: isDark ? '#ecf0f1' : 'inherit'
              }}>, </Typography>
              <Typography component="span" sx={{
                fontSize: '15px',
                color: isDark ? '#ecf0f1' : 'inherit'
              }}>{intervenant.rank}</Typography>
              <Typography component="span" sx={{
                fontSize: '15px',
                color: isDark ? '#ecf0f1' : 'inherit'
              }}>{index < array.length - 1 ? ', ' : ''}</Typography>
            </Typography>
           ))}
         <br/>
        attaché{reportData.intervenants && 
          reportData.intervenants.filter(intervenant => intervenant.sign === true).length > 1 ? 's' : ''} au service {reportData.unit} de la <SettingsFieldDisplay fieldName="zoneNamePV" />,<br/>
        portons à la connaissance de votre office :
        <br/>
        <br/>
      </Typography>

      {sortedRubrics.map((rubric, index) => (
        <div 
          key={rubric.id}
          data-rubric-id={rubric.id}
          ref={el => rubricRefs.current[rubric.id] = el}
        >
            <Divider sx={{ 
              my: 1,
              borderColor: isDark ? '#2d3f4c' : 'rgba(0, 0, 0, 0.12)'
            }} />

          <Typography variant="subtitle1" sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            {rubric.title}
          </Typography>
          
          <RichTextDisplay content={rubric.content} isDark={isDark} />
          <br/>
        </div>
      ))}
        <Typography variant="body1" sx={{
            whiteSpace: 'pre-wrap',
            '& p': { marginTop: 0 },
            textAlign: 'right',
            fontSize: '15px',
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            <br/><br/>
            Dont acte, clos le {formatEndDate(reportData.EndDate)}<br/>
            
            {reportData.intervenants && reportData.intervenants
              .filter(intervenant => intervenant.sign === true)
              .map((intervenant, index) => (
                <Box key={index} sx={{ display: 'flex', mt: 1, justifyContent: 'flex-end' }}>
                  <Typography component="span" sx={{
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>•{' '}</Typography>
                  <Typography component="span" sx={{
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>{intervenant.rank}{' '}</Typography>
                  <Typography component="span" sx={{
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>
                    {intervenant.lastname}{' '}
                  </Typography>
                  <Typography component="span" sx={{
                    fontWeight: 'bold',
                    fontSize: '15px',
                    color: isDark ? '#ecf0f1' : 'inherit'
                  }}>
                    {intervenant.firstname}
                  </Typography>
                  <br/><br/>
                </Box>
              ))}
          </Typography>
    </Box>
  );
}

export default Read;



